/* eslint-disable @typescript-eslint/naming-convention */
import { Directive, OnInit } from '@angular/core';
import {
  distinctUntilChanged,
  map,
  skip,
  startWith,
  takeUntil,
} from 'rxjs/operators';
import { uniqueObjectsByFields } from '@ups/xplat/utils';
import {
  dynamicFavoriteCompanyId,
  dynamicRecommended,
  dynamicTypeaheadEmployeeFilter,
} from '../../utils';
import { DynamicTypeaheadBaseComponent } from '../dynamic-typeahead/dynamic-typeahead.base-component';

@Directive()
export abstract class DynamicTypeaheadEmployeeBaseComponent
  extends DynamicTypeaheadBaseComponent
  implements OnInit
{
  /** PR 6191 review finding 4: the key of the contact the pre-fill wrote, until the company changes. */
  private autoFillKey: unknown = null;

  queryFilter = (query: string, data: unknown) =>
    dynamicTypeaheadEmployeeFilter(query, data, this.customQueryParams);

  ngOnInit() {
    if (this.config?.options?.distinctHRRef) {
      this.config.options.valueQueryParamName = 'hrRefs';
    }
    super.ngOnInit();
  }

  /**
   * Bug 35871: with `prefillSkipsCustomQueryParams`, the saved-value lookup (the call carrying
   * `valueQueryParamName`) runs without the host's `customQueryParams`, so a saved employee who has
   * since gone inactive still resolves under an `IsActive: true` host; a typed search keeps them. The
   * base builds the request params synchronously inside this call, so they are withheld only for it.
   */
  /** US 36258: the company id the search sends as `favoriteVpCompanyID`; null when the option is off or empty. */
  public favoriteVpCompanyId(): number | null {
    return dynamicFavoriteCompanyId(
      this.group,
      this.config?.options?.favoriteCompanyFormControl
    );
  }

  /** With `favoriteCompanyFormControl` the pre-fill needs a company (and a new form), not preSelectSingleFavorite. */
  override shouldPreSelectSingleFavorite(): boolean {
    if (!this.config?.options?.favoriteCompanyFormControl) {
      return super.shouldPreSelectSingleFavorite();
    }
    return (
      this.config.options.editing !== true && this.favoriteVpCompanyId() != null
    );
  }

  /** With a company: exactly one recommended contact (favorites never pre-fill); otherwise the single favorite. */
  override ngAfterViewInit() {
    super.ngAfterViewInit();
    this.watchFavoriteCompany();
  }

  override filterChangeItem(
    $event: Parameters<DynamicTypeaheadBaseComponent['filterChangeItem']>[0],
    valueQueryParamName?: string
  ) {
    if (
      !valueQueryParamName ||
      !this.config?.options?.prefillSkipsCustomQueryParams
    ) {
      return super.filterChangeItem($event, valueQueryParamName);
    }
    const customQueryParams = this.customQueryParams;
    this.customQueryParams = undefined;
    try {
      return super.filterChangeItem($event, valueQueryParamName);
    } finally {
      this.customQueryParams = customQueryParams;
    }
  }

  public getCompaniesList(dataItem: {
    Company?: number;
    CompanyDetails: {
      Company?: number;
      IsActive?: boolean | null;
    }[];
  }) {
    if ((dataItem.CompanyDetails || []).length) {
      return this.normalizeActiveFlags(dataItem.CompanyDetails);
    }
    return [{ Company: dataItem.Company || 'n/a', IsActive: false }];
  }

  public getEMDesignationList(dataItem: {
    EMDesignationName?: string;
    CompanyDetails: {
      EMDesignationName?: string;
      IsActive?: boolean | null;
    }[];
  }) {
    if ((dataItem.CompanyDetails || []).length) {
      const uniqueDesignations = uniqueObjectsByFields(
        dataItem.CompanyDetails,
        ['EMDesignationName']
      );
      const designations =
        uniqueDesignations.length > 1
          ? dataItem.CompanyDetails
          : uniqueDesignations;
      return this.normalizeActiveFlags(designations);
    }
    return [
      {
        EMDesignationName: dataItem.EMDesignationName || 'n/a',
        IsActive: false,
      },
    ];
  }

  public getFileClassList(dataItem: {
    FileClassName?: string;
    CompanyDetails: {
      FileClassName?: string;
      IsActive?: boolean | null;
    }[];
  }) {
    if ((dataItem.CompanyDetails || []).length) {
      const uniqueClasses = uniqueObjectsByFields(dataItem.CompanyDetails, [
        'FileClassName',
      ]);
      const classes =
        uniqueClasses.length > 1 ? dataItem.CompanyDetails : uniqueClasses;
      return this.normalizeActiveFlags(classes);
    }
    return [
      { FileClassName: dataItem.FileClassName || 'n/a', IsActive: false },
    ];
  }

  /**
   * US 36258: a company change reloads the list so a lone recommended contact can pre-fill an empty
   * field. A user's pick is left alone; a contact the pre-fill chose follows the company (cleared,
   * then re-filled from the new company's list). The user can still search anyone.
   */
  protected watchFavoriteCompany() {
    if (!this.config?.options?.favoriteCompanyFormControl || !this.group) {
      return;
    }
    this.group.valueChanges
      .pipe(
        map(() => this.favoriteVpCompanyId()),
        startWith(this.favoriteVpCompanyId()),
        distinctUntilChanged(),
        skip(1),
        takeUntil(this.destroy$)
      )
      .subscribe((companyId) => {
        const autoFilled = this.holdsAutoFill();
        if (autoFilled) {
          this.clearAutoFill();
        }
        if (
          companyId != null &&
          this.shouldPreSelectSingleFavorite() &&
          (autoFilled || this.hasNoSelection())
        ) {
          this.filterChangeItem({
            query: '',
            extension: { applyData: () => {} },
          });
        }
      });
  }

  protected override singlePreSelectCandidate(data: Array<unknown>) {
    if (!this.config?.options?.favoriteCompanyFormControl) {
      return super.singlePreSelectCandidate(data);
    }
    const recommended = dynamicRecommended(data);
    const candidate =
      this.shouldPreSelectSingleFavorite() && recommended.length === 1
        ? recommended[0]
        : null;
    if (candidate) {
      this.autoFillKey = this.selectionKey(candidate);
    }
    return candidate;
  }

  private selectionKey(value: unknown): unknown {
    const single = Array.isArray(value)
      ? value.length === 1
        ? value[0]
        : null
      : value;
    if (single != null && typeof single === 'object') {
      return (single as Record<string, unknown>)[
        this.config?.valueProperty || 'HRRef'
      ];
    }
    return single;
  }

  private holdsAutoFill(): boolean {
    const name = this.config?.formControlName;
    const key = this.selectionKey(
      name ? this.group?.controls?.[name]?.value : null
    );
    return key != null && key === this.autoFillKey;
  }

  private clearAutoFill() {
    this.autoFillKey = null;
    this.updateValue(this.config?.options?.isMultiSelect ? [] : null);
    this.resetSelectedItems();
  }

  private normalizeActiveFlags<T extends { IsActive?: boolean | null }>(
    items: T[]
  ): Array<T & { IsActive: boolean }> {
    items.forEach((item) => {
      item.IsActive = item.IsActive === true;
    });
    return items as Array<T & { IsActive: boolean }>;
  }
}
